<template>
  <div class="login-page">
    <div class="login-container">
      <!-- Logo and Header -->
      <div class="login-header">
        <img
          src="@/assets/DPD BELL logo_w.png"
          alt="Philadelphia Department of Planning and Development"
          class="login-logo"
        >
        <h1 class="login-title">Eviction Diversion Program</h1>
        <p class="login-subtitle">The button below will redirect you to sign in or sign up to access your account</p>
      </div>

      <!-- Login Card -->
      <div class="login-card">
        <!-- Loading State -->
        <div v-if="isLoading" class="has-text-centered">
          <div class="spinner"></div>
          <p class="loading-text">Processing authentication...</p>
        </div>

        <!-- Login Button -->
        <div v-else-if="!isAuthenticated">
          <button
            class="button is-fullwidth sign-in-button"
            @click="handleSignIn"
          >
            <span class="icon">
              <i class="fas fa-sign-in-alt"></i>
            </span>
            <span>Authenticate</span>
          </button>

          <div class="login-links">
            <p class="link-item">
              <span class="link-label">Forgot your password?</span>
              <a
                href="#"
                class="link-action"
                @click.prevent="handleForgotPassword"
              >Reset here
              </a>
            </p>
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="error-message">
            <span class="icon">
              <i class="fas fa-exclamation-circle"></i>
            </span>
            <span>{{ errorMessage }}</span>
          </div>
        </div>

        <!-- Already Authenticated -->
        <div v-else class="has-text-centered authenticated-state">
          <span class="icon has-text-phanatic-green success-icon">
            <i class="fas fa-check-circle fa-3x"></i>
          </span>
          <p class="success-title">You're signed in!</p>
          <p class="success-text">Redirecting to dashboard...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Login',
  data() {
    return {
      isLoading: false,
      errorMessage: '',
    };
  },
  computed: {
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated'];
    },
  },
  watch: {
    // Watch for authentication state changes and redirect
    isAuthenticated(newValue) {
      if (newValue) {
        console.log('User authenticated, redirecting to dashboard');
        this.$router.push('/dashboard').catch(err => {
          console.error('Navigation error:', err);
        });
      }
    },
  },
  async created() {
    // If already authenticated on page load, redirect immediately
    if (this.isAuthenticated) {
      this.$router.push('/dashboard').catch(err => {
        console.error('Navigation error:', err);
      });
      return;
    }

    // Handle redirect from Azure B2C
    this.isLoading = true;
    try {
      await this.$store.dispatch('auth/handleRedirect');
    } catch (error) {
      console.error('Error handling authentication redirect:', error);

      // Extract error code if available
      const errorCode = error.message || 'UNKNOWN_ERROR';
      // Get SSO ID from getter for debugging
      const ssoId = this.$store.getters['auth/ssoId'] || 'Not Available';

      this.errorMessage = `There was an error processing your information. Please contact a developer.\nError Code: ${errorCode}\nSSO ID: ${ssoId}`;
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async handleSignIn() {
      try {
        this.errorMessage = '';
        this.isLoading = true;
        await this.$store.dispatch('auth/signIn');
      } catch (error) {
        console.error('Sign in error:', error);
        this.errorMessage = 'Unable to initiate sign in. Please try again.';
        this.isLoading = false;
      }
    },
    async handleForgotPassword() {
      try {
        this.errorMessage = '';
        this.isLoading = true;
        await this.$store.dispatch('auth/forgotPassword');
      } catch (error) {
        console.error('Forgot password error:', error);
        this.errorMessage = 'Unable to initiate password reset. Please try again.';
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Login page - no wrapper styles needed, uses main content area */
.login-page {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 3rem 1rem;
}

/* Container for logo, title and card - unified blue background */
.login-container {
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #0f4d90;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
}

/* Logo and Header Section */
.login-header {
  text-align: center;
  margin-bottom: 2.5rem;
  width: 100%;
}

.login-logo {
  width: 200px;
  height: auto;
  display: block;
  margin: 0 auto 1.5rem auto;
}

.login-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.75rem 0;
  font-family: 'Montserrat', sans-serif;
  line-height: 1.3;
}

.login-subtitle {
  font-size: 1rem;
  color: #ffffff;
  margin: 0;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  opacity: 0.95;
}

/* Card section - content wrapper */
.login-card {
  width: 100%;
}

/* Sign In Button - Prominent CTA */
.sign-in-button {
  background-color: #2176d2 !important;
  border-color: #2176d2 !important;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 1.125rem;
  padding: 1.25rem 2rem !important;
  height: auto !important;
  min-height: 3.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  font-family: 'Open Sans', sans-serif;
  margin-bottom: 1.5rem;
}

.sign-in-button:hover {
  background-color: #1a5ba8 !important;
  border-color: #1a5ba8 !important;
  color: #ffffff !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
  transform: translateY(-1px);
}

.sign-in-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Links Section */
.login-links {
  margin-top: 1.5rem;
  text-align: center;
}

.link-item {
  margin-bottom: 0;
  font-size: 0.95rem;
  color: #ffffff;
  font-family: 'Open Sans', sans-serif;
}

.link-label {
  margin-right: 0.25rem;
  color: #ffffff;
  opacity: 0.95;
}

.link-action {
  color: #ffffff;
  text-decoration: underline;
  font-weight: 600;
  transition: color 0.2s ease;
}

.link-action:hover {
  color: #daedfe;
  text-decoration: underline;
}

/* Error Message */
.error-message {
  background: rgba(254, 231, 231, 0.95);
  color: #cc3000;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  white-space: pre-line;
}

/* Loading State */
.spinner {
  border: 4px solid #f0f0f0;
  border-top: 4px solid #2176d2;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 1rem;
  color: #ffffff;
  font-family: 'Open Sans', sans-serif;
  font-size: 0.95rem;
}

/* Authenticated State */
.authenticated-state {
  padding: 2rem 0;
}

.success-icon {
  display: block;
  margin-bottom: 1rem;
  color: #dcf8d8;
}

.success-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
  font-family: 'Montserrat', sans-serif;
}

.success-text {
  color: #ffffff;
  font-family: 'Open Sans', sans-serif;
  opacity: 0.95;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .login-container {
    padding: 2rem 1.5rem;
  }

  .login-title {
    font-size: 1.5rem;
  }

  .sign-in-button {
    font-size: 1rem;
    padding: 1rem 1.5rem !important;
  }
}
</style>
